/**
 * The end-of-lesson survey, with the role asked first.
 *
 * One tap decides which set of questions is worth asking: a teacher is judging
 * whether to put this in front of thirty students, a student is reporting
 * whether they understood it. Averaging those two into one score would produce
 * a number that means nothing.
 *
 * It stays collapsed until asked for. A five-question form that unfurls under
 * every lesson trains people to scroll past it, and the response we lose is the
 * considered one.
 */

import { useState } from 'react';
import { MessageSquarePlus, GraduationCap, Backpack, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { glassPanel, inkMuted } from './learnShared';
import { FeedbackSurvey } from './FeedbackSurvey';
import { VIDEO_STUDENT, VIDEO_TEACHER } from './feedbackForms';
import { useLearnAuth } from './LearnAuthProvider';

export function LessonSurveyPanel({
  shareToken,
  courseId,
  chapter,
}: {
  shareToken: string;
  courseId?: string | null;
  chapter?: string | null;
}) {
  const { profile } = useLearnAuth();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<'STUDENT' | 'TEACHER' | null>(null);
  const [sent, setSent] = useState(false);

  // A second response is welcome: a learner who rewatches, or a teacher who has
  // now used the lesson in class, knows something they did not the first time.
  if (sent && !open) {
    return (
      <div className={cn(glassPanel, 'flex flex-wrap items-center justify-between gap-3 p-4')}>
        <p className={cn('text-[13px]', inkMuted)}>Thank you — we have your feedback on this lesson.</p>
        <button
          onClick={() => setOpen(true)}
          className="rounded-lg text-[13px] font-semibold text-[#5B2EE5] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:text-[#BFA8FA]"
        >
          Send more feedback
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={cn(
          glassPanel,
          'flex w-full items-center gap-3 p-4 text-left transition-all hover:-translate-y-0.5 hover:bg-white/[0.92] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:hover:bg-[#181A2A]/[0.85]',
        )}
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#5B2EE5]/10">
          <MessageSquarePlus className="h-5 w-5 text-[#5B2EE5] dark:text-[#BFA8FA]" />
        </span>
        <span className="min-w-0">
          <span className="block text-[14.5px] font-semibold">Tell us about this lesson</span>
          <span className={cn('block text-[12.5px]', inkMuted)}>Five questions, under a minute.</span>
        </span>
      </button>
    );
  }

  if (!role) {
    return (
      <div className={cn(glassPanel, 'relative p-5')}>
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          className={cn('absolute right-3 top-3 rounded-lg p-1.5 hover:text-[#191831] dark:hover:text-white', inkMuted)}
        >
          <X className="h-4 w-4" />
        </button>
        <p className="font-serif text-[20px] leading-[1.28] tracking-tight">First — who are you?</p>
        <p className={cn('mt-1.5 text-[13.5px]', inkMuted)}>
          It changes what we ask. A teacher and a student are judging different things.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2.5">
          <RoleButton icon={<GraduationCap className="h-5 w-5" />} label="A teacher" onClick={() => setRole('TEACHER')} />
          <RoleButton icon={<Backpack className="h-5 w-5" />} label="A student" onClick={() => setRole('STUDENT')} />
        </div>
      </div>
    );
  }

  return (
    <FeedbackSurvey
      form={role === 'TEACHER' ? VIDEO_TEACHER : VIDEO_STUDENT}
      role={role}
      shareToken={shareToken}
      courseId={courseId ?? null}
      chapter={chapter ?? null}
      learnerAccountId={profile?.id ?? null}
      prefillName={profile?.displayName ?? null}
      prefillEmail={profile?.email ?? null}
      askIdentity={role === 'TEACHER'}
      onDone={() => {
        setSent(true);
        setOpen(false);
        setRole(null);
      }}
    />
  );
}

function RoleButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-xl border border-white/70 bg-white/70 px-3 py-4 text-[14px] font-semibold transition-all hover:-translate-y-0.5 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:border-white/10 dark:bg-white/[0.07] dark:hover:bg-white/[0.12]"
    >
      <span className="text-[#5B2EE5] dark:text-[#BFA8FA]">{icon}</span>
      {label}
    </button>
  );
}
